import React, { useEffect, useId, useState } from 'react';
import Button, { buttonClass } from '../ui/Button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../pilot-ui/collapsible';
import { Link } from 'react-router';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Alert, AlertListResponse, Device } from '../../types';
import { deviceClipLabel } from '../../utils/clipLabel';

/**
 * FS-667 — a small box of one device's open alerts, at the top of the
 * Analysis tab so a quick review of a device does not miss a humidity or
 * runtime alert that only the Alerts page would have shown.
 *
 * Two independent reads of the alerts REST list, both keyed to the device:
 * the open rows (status=active, one page — alerts.emit keeps at most one open
 * row per device+type, and there are under ten types, so a page of 50 is the
 * whole list) and a closed count (status=closed, limit=1, `total` only). The
 * count is a footer; its failure hides the footer and nothing else.
 *
 * A snapshot: fetched on mount, device change and permission change, never
 * polled. Renders nothing without read_alerts — the Analysis tab itself has
 * no alert permission, so the gate lives here.
 *
 * FS-722: the closed count is a button. Pressing it fetches one page of the
 * device's closed rows (status=closed, limit=50) and lists them under the
 * footer; pressing again hides them. The list belongs to the device it was
 * opened for, so moving to another device closes it.
 */

const SEV_ORDER: Record<string, number> = { urgent: 0, warning: 1, info: 2 };
const SEV_CLASSES: Record<string, string> = {
  urgent: 'border-status-error-border bg-status-error text-status-error-foreground',
  warning: 'border-status-warning-border bg-status-warning-strong text-status-warning-foreground',
  info: 'border-status-info-border bg-status-info text-status-info-foreground',
};
const SEV_FALLBACK = 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground';

const OPEN_PAGE = 50;
const CLOSED_PAGE = 50;

type OpenList =
  | { status: 'loading' }
  | { status: 'ready'; rows: Alert[]; total: number }
  | { status: 'unavailable' };

type ClosedList =
  | { status: 'loading' }
  | { status: 'ready'; rows: Alert[]; total: number }
  | { status: 'unavailable' };

interface BoxState {
  // The device the snapshot belongs to. A render for another device shows
  // the loading state rather than this one's rows.
  deviceId: number;
  open: OpenList;
  // Positive after a successful closed count; null while loading, at zero,
  // and on failure — the footer reads null as "say nothing".
  closedTotal: number | null;
}

// The list envelope, checked at runtime: a misconfigured URL can answer with
// an HTML page, and TypeScript's response type proves nothing about it.
const parseEnvelope = (body: unknown): AlertListResponse | null => {
  if (typeof body !== 'object' || body === null) return null;
  const env = body as Partial<AlertListResponse>;
  if (typeof env.total !== 'number' || !Number.isFinite(env.total)) return null;
  if (!Array.isArray(env.rows)) return null;
  return env as AlertListResponse;
};

const openedTime = (a: Alert): number => {
  if (typeof a.opened_at !== 'string') return Number.NaN;
  return new Date(a.opened_at).getTime();
};

// Urgent first, then warning, then info; newest first within a severity;
// id as the tie-break so two rows opened together keep one order.
const sortForDisplay = (rows: Alert[]): Alert[] =>
  [...rows].sort((a, b) => {
    const sev = (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3);
    if (sev !== 0) return sev;
    const ta = openedTime(a);
    const tb = openedTime(b);
    // An unparseable stamp sorts after every real one.
    const va = Number.isNaN(ta) ? Number.NEGATIVE_INFINITY : ta;
    const vb = Number.isNaN(tb) ? Number.NEGATIVE_INFINITY : tb;
    if (va !== vb) return vb - va;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });

const stampTime = (stamp: unknown): number =>
  typeof stamp === 'string' ? new Date(stamp).getTime() : Number.NaN;

// UTC calendar day, the same on every operator's machine. Never throws: an
// invalid Date would throw from toISOString, so it is checked first.
const utcDay = (t: number): string =>
  Number.isNaN(t) ? '—' : new Date(t).toISOString().slice(0, 10);

const openedUtcDay = (a: Alert): string => utcDay(openedTime(a));

// Newest close first; an unparseable close stamp falls back to when the
// alert opened, then sorts after every real one.
const sortClosed = (rows: Alert[]): Alert[] =>
  [...rows].sort((a, b) => {
    const key = (x: Alert) => {
      const t = stampTime(x.closed_at);
      const v = Number.isNaN(t) ? openedTime(x) : t;
      return Number.isNaN(v) ? Number.NEGATIVE_INFINITY : v;
    };
    const d = key(b) - key(a);
    if (d !== 0) return d;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });

const closeReason = (a: Alert): string =>
  (typeof a.close_reason === 'string' && a.close_reason.trim()
    ? a.close_reason.replace(/_/g, ' ')
    : '—');

// Whether the homeowner was told: the sent stamps are the only record of it.
const deliveryLabel = (a: Alert): string => {
  const push = !!a.fcm_sent_at;
  const email = !!a.email_sent_at;
  if (push && email) return 'push + email';
  if (push) return 'push';
  if (email) return 'email';
  return 'not sent';
};

const alertTitle = (a: Alert): string =>
  (typeof a.title === 'string' && a.title.trim())
    || (typeof a.type === 'string' ? a.type.replace(/_/g, ' ') : '')
    || 'alert';

// FS-825 — the Clip label, so two Clips in one home read differently here too.
const deviceLabel = (device: Device) => `${deviceClipLabel(device)} (#${device.id})`;

const OpenAlertsBox: React.FC<{ device: Device }> = ({ device }) => {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('read_alerts');
  const headingId = useId();
  const [state, setState] = useState<BoxState | null>(null);
  const closedListId = useId();
  // The device the closed list was opened for; any other device shows it shut.
  const [closedOpenFor, setClosedOpenFor] = useState<number | null>(null);
  const [closedList, setClosedList] = useState<{ deviceId: number; list: ClosedList } | null>(null);
  const showClosed = closedOpenFor === device.id;

  // A device switch shuts the closed list, so coming back to this device
  // does not reopen it by surprise.
  useEffect(() => {
    setClosedOpenFor(null);
  }, [device.id]);

  useEffect(() => {
    if (!canRead) return undefined;
    const deviceId = device.id;
    // Every answer checks this before touching state, so a device switch,
    // deselection, permission loss or unmount silently drops what was in
    // flight — the newer device's effect owns the screen from here on.
    let cancelled = false;

    // Trimmed: an env var that is only spaces is "not configured", not a
    // relative URL to sit on until it times out.
    const url = config.alertsApiUrl.trim();
    if (!url) {
      setState({ deviceId, open: { status: 'unavailable' }, closedTotal: null });
      return undefined;
    }
    setState({ deviceId, open: { status: 'loading' }, closedTotal: null });

    const patch = (change: Partial<BoxState>) => {
      if (cancelled) return;
      setState((prev) => (prev && prev.deviceId === deviceId ? { ...prev, ...change } : prev));
    };

    axiosInstance
      .get<AlertListResponse>(url, {
        params: { device_id: deviceId, status: 'active', limit: OPEN_PAGE, offset: 0 },
      })
      .then((res) => {
        const env = parseEnvelope(res.data);
        patch({ open: env ? { status: 'ready', rows: env.rows, total: env.total } : { status: 'unavailable' } });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load open alerts:', err);
        patch({ open: { status: 'unavailable' } });
      });

    axiosInstance
      .get<AlertListResponse>(url, {
        params: { device_id: deviceId, status: 'closed', limit: 1, offset: 0 },
      })
      .then((res) => {
        const env = parseEnvelope(res.data);
        patch({ closedTotal: env && env.total > 0 ? env.total : null });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to count closed alerts:', err);
        patch({ closedTotal: null });
      });

    return () => {
      cancelled = true;
    };
  }, [device.id, canRead]);

  useEffect(() => {
    if (!canRead || !showClosed) return undefined;
    const deviceId = device.id;
    let cancelled = false;
    const url = config.alertsApiUrl.trim();
    if (!url) {
      setClosedList({ deviceId, list: { status: 'unavailable' } });
      return undefined;
    }
    setClosedList({ deviceId, list: { status: 'loading' } });
    axiosInstance
      .get<AlertListResponse>(url, {
        params: { device_id: deviceId, status: 'closed', limit: CLOSED_PAGE, offset: 0 },
      })
      .then((res) => {
        if (cancelled) return;
        const env = parseEnvelope(res.data);
        setClosedList({
          deviceId,
          list: env ? { status: 'ready', rows: env.rows, total: env.total } : { status: 'unavailable' },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load closed alerts:', err);
        setClosedList({ deviceId, list: { status: 'unavailable' } });
      });
    return () => {
      cancelled = true;
    };
  }, [device.id, canRead, showClosed]);

  if (!canRead) return null;

  const current = state && state.deviceId === device.id ? state : null;
  const open: OpenList = current?.open ?? { status: 'loading' };
  const closedTotal = current?.closedTotal ?? null;
  const rows = open.status === 'ready' ? sortForDisplay(open.rows) : [];
  const notShown = open.status === 'ready' ? Math.max(0, open.total - open.rows.length) : 0;
  const closed: ClosedList = closedList && closedList.deviceId === device.id
    ? closedList.list
    : { status: 'loading' };
  const closedRows = closed.status === 'ready' ? sortClosed(closed.rows) : [];
  const closedNotShown = closed.status === 'ready' ? Math.max(0, closed.total - closed.rows.length) : 0;

  return (
    <section
      aria-labelledby={headingId}
      className="border rounded-md p-3 text-sm mb-4"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1">
        {/* A long unbroken description is cut with an ellipsis rather than
            pushed past the box; the full label is in the hover text. */}
        <h3 id={headingId} aria-label={`Open alerts ${deviceLabel(device)}`} className="font-semibold min-w-0 flex-1 truncate">
          Open alerts
          <span className="ml-1 font-normal text-gray-600" title={deviceLabel(device)}>
            {deviceLabel(device)}
          </span>
        </h3>
        {open.status === 'ready' && (
          <span
            aria-label={`${open.total} open`}
            className={`inline-block min-w-6 px-1.5 rounded-full text-center text-xs font-semibold ${
              open.total > 0 ? 'border border-status-error-border bg-status-error text-status-error-foreground' : 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground'
            }`}
          >
            {open.total}
          </span>
        )}
      </div>

      {open.status === 'loading' && <p className="text-muted-foreground">Loading alerts…</p>}
      {open.status === 'unavailable' && <p className="text-muted-foreground">Alerts unavailable</p>}
      {open.status === 'ready' && rows.length === 0 && (
        <p className="text-muted-foreground">No open alerts</p>
      )}
      {open.status === 'ready' && rows.length > 0 && (
        // The list scrolls down on its own so the heading and footer stay put;
        // never sideways (overflow-y alone would make the x axis scroll too).
        <ul className="max-h-48 overflow-y-auto overflow-x-hidden space-y-1">
          {rows.map((a, i) => (
            // A row wraps when it must: the title keeps a readable minimum and
            // the date and admin-only markers drop under it instead of
            // squeezing the title to nothing.
            <li key={a.id ?? `row-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span
                className={`shrink-0 text-xs font-semibold px-1.5 rounded-sm border ${
                  SEV_CLASSES[a.severity] ?? SEV_FALLBACK
                }`}
              >
                {a.severity}
              </span>
              <span className="min-w-32 flex-1 truncate" title={alertTitle(a)}>
                {alertTitle(a)}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap" title="opened (UTC)">
                {openedUtcDay(a)}
              </span>
              {a.internal_only && (
                // The flag records who the alert is for, not whether the app
                // was told — the box has no view of delivery, so the hover
                // text must not claim one.
                <span
                  className="shrink-0 text-xs px-1.5 rounded-sm border border-status-neutral-border bg-status-neutral text-status-neutral-foreground"
                  title="Internal alert — for operators"
                >
                  admin-only
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {notShown > 0 && (
        <p className="text-xs text-muted-foreground mt-1">and {notShown} more on the Alerts page</p>
      )}

      <Collapsible open={showClosed} onOpenChange={(open) => setClosedOpenFor(open ? device.id : null)}>
        <div className="flex items-baseline justify-between gap-2 mt-2 text-xs">
          {closedTotal !== null ? (
            <CollapsibleTrigger
              render={<Button variant="link" size="sm" title={showClosed ? 'Hide this device\'s closed alerts' : 'Show this device\'s closed alerts'} />}
              aria-expanded={showClosed}
              aria-controls={closedListId}
            >
              {`${closedTotal} closed`}
            </CollapsibleTrigger>
          ) : (
            <span />
          )}
          <Link to="/alerts" className={`${buttonClass('link', 'sm')} whitespace-nowrap`} title="Open the alerts page for all devices">
            All alerts →
          </Link>
        </div>

        <CollapsibleContent id={closedListId} className="mt-2 text-xs">
          {closed.status === 'loading' && <p className="text-muted-foreground">Loading closed alerts…</p>}
          {closed.status === 'unavailable' && <p className="text-muted-foreground">Closed alerts unavailable</p>}
          {closed.status === 'ready' && closedRows.length === 0 && (
            <p className="text-muted-foreground">No closed alerts</p>
          )}
          {closed.status === 'ready' && closedRows.length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              <table aria-label={`Closed alerts for ${deviceLabel(device)}`} className="w-full">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="pr-2 font-medium">Alert</th>
                    <th className="pr-2 font-medium whitespace-nowrap" title="UTC day">Opened</th>
                    <th className="pr-2 font-medium whitespace-nowrap" title="UTC day">Closed</th>
                    <th className="pr-2 font-medium">Reason</th>
                    <th className="font-medium" title="Whether a push notification or email went to the homeowner">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {closedRows.map((a, i) => (
                    <tr key={a.id ?? `closed-${i}`}>
                      <td className="pr-2 py-0.5 max-w-48 truncate" title={alertTitle(a)}>{alertTitle(a)}</td>
                      <td className="pr-2 py-0.5 whitespace-nowrap text-gray-600">{openedUtcDay(a)}</td>
                      <td className="pr-2 py-0.5 whitespace-nowrap text-gray-600">{utcDay(stampTime(a.closed_at))}</td>
                      <td className="pr-2 py-0.5 text-gray-600">{closeReason(a)}</td>
                      <td className="py-0.5 whitespace-nowrap text-gray-600">{deliveryLabel(a)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {closedNotShown > 0 && (
            <p className="text-muted-foreground mt-1">and {closedNotShown} more on the Alerts page</p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};

export default OpenAlertsBox;
